import React from "react";
import { CheckCircle2, MinusCircle, Sparkles } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

const TARGETS = [
  { v: "name", label: "Nom du joueur" },
  { v: "position", label: "Poste" },
  { v: "club", label: "Club" },
  { v: "age", label: tfr("Âge") },
  { v: "level", label: "Niveau" },
  { v: "status", label: tfr("Statut") },
  { v: "phone", label: tfr("Téléphone") },
  { v: "email", label: "Email" },
  { v: "__skip__", label: "— Ne pas importer —" },
];

const TARGET_LABEL = TARGETS.reduce((acc, t) => ({ ...acc, [t.v]: t.label }), {});

/**
 * ImportMapping — Step 2.
 * Displays each source column with a dropdown to map to an Athlynx field.
 * Pre-filled by Claude IA. User can override.
 */
export default function ImportMapping({ headers = [], mapping = {}, sample = {}, aiUsed, onChange }) {
  const mappedCount = Object.values(mapping).filter((v) => v && v !== "__skip__").length;
  const requiredOk = Object.values(mapping).includes("name");

  const update = (col, val) => {
    onChange?.({ ...mapping, [col]: val === "__skip__" ? null : val });
  };

  return (
    <div className="space-y-4" data-testid="import-step-mapping">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {aiUsed && (
            <Badge className="bg-info/10 text-info border-info/30 gap-1.5" data-testid="mapping-ai-badge">
              <Sparkles className="h-3 w-3" /> Détecté par IA
            </Badge>
          )}
          <Badge variant="outline" className="border-border dark:border-border text-muted-foreground">
            {mappedCount} / {headers.length} {headers.length > 1 ? tfr("colonnes associées") : tfr("colonne associée")}
          </Badge>
        </div>
        {!requiredOk && (
          <Badge className="bg-danger/10 text-danger border-danger/30">
            Le champ « Nom » est requis
          </Badge>
        )}
      </div>

      <div className="rounded-2xl border border-border dark:border-border bg-card dark:bg-surface/40 overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2.5 text-[10px] uppercase tracking-wider text-muted-foreground dark:text-muted-foreground/70 border-b border-border/70 dark:border-border bg-secondary/50 dark:bg-secondary/30">
          <div className="col-span-5">{tfr("Votre colonne")}</div>
          <div className="col-span-2 text-center hidden md:block">→</div>
          <div className="col-span-7 md:col-span-5">Champ Athlynx</div>
        </div>
        <div className="divide-y divide-border/70 dark:divide-border/60">
          {headers.map((h) => {
            const cur = mapping[h];
            const isMapped = cur && cur !== "__skip__";
            return (
              <div
                key={h}
                className="grid grid-cols-12 items-center gap-2 px-4 py-2.5 hover:bg-accent/40 dark:hover:bg-secondary/50 dark:bg-secondary/30 transition-colors"
                data-testid={`map-row-${h}`}
              >
                <div className="col-span-12 md:col-span-5 min-w-0">
                  <div className="text-sm font-medium truncate flex items-center gap-2">
                    {isMapped ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0" />
                    ) : (
                      <MinusCircle className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
                    )}
                    <span className="truncate">{h}</span>
                  </div>
                  {sample[h] && (
                    <div className="text-[11px] text-muted-foreground dark:text-muted-foreground/70 truncate mt-0.5 ml-5">
                      ex : {String(sample[h]).slice(0, 40)}
                    </div>
                  )}
                </div>
                <div className="hidden md:block col-span-2 text-center text-muted-foreground/40 text-sm">→</div>
                <div className="col-span-12 md:col-span-5">
                  <Select
                    value={cur || "__skip__"}
                    onValueChange={(v) => update(h, v)}
                  >
                    <SelectTrigger
                      className={cn(
                        "h-9 text-sm border-border dark:border-border bg-background/60",
                        isMapped && "text-foreground",
                        !isMapped && "text-muted-foreground"
                      )}
                      data-testid={`map-select-${h}`}
                    >
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {TARGETS.map((t) => (
                        <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export { TARGET_LABEL };
